import { ProductDimensionValueInventoryAvailability, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getExtensionPropertyBoolean } from '../../utilities/extension-property.util';
import {
    getInventoryLevelCodeFromDimensionValue,
    IDimensionForSelectedVariant,
    InventoryLevels,
    InventoryLevelValues,
    IProductInventoryInformation
} from '@msdyn365-commerce-modules/retail-actions';

export interface IInventoryStatus {
    type: 'to-order' | 'central-warehouse' | 'in-store' | 'out-of-stock' | 'select-variant' | 'checking';
    canAddToCart: boolean;
    storeNames?: string[];
}

/**
 * Central warehouse ID constant
 */
export const CENTRAL_WAREHOUSE_ID = '1001';

/**
 * True when the product is a master with at least one dimension not yet chosen.
 */
export function hasUnselectedDimensions(product: SimpleProduct | undefined): boolean {
    return !!product?.Dimensions?.some(dimension => !dimension.DimensionValue?.Value);
}

/**
 * True when the dimension value's estimated availability code is out of stock.
 * Returns false when no code is available (inventoryThreshold mode), so unknown counts as available.
 */
export function isDimensionValueOutOfStock(
    value: ProductDimensionValueInventoryAvailability,
    inventoryLevel: InventoryLevels | undefined
): boolean {
    return getInventoryLevelCodeFromDimensionValue(value, inventoryLevel) === InventoryLevelValues.outOfStock;
}

/**
 * True when any chosen dimension value is flagged out of stock.
 * @param productDimensions - Dimensions with per-value availability.
 * @param selection - Chosen value record id per dimension type.
 * @param inventoryLevel - App config inventory level setting.
 */
export function isSelectionOutOfStock(
    productDimensions: IDimensionForSelectedVariant[] | undefined,
    selection: { [dimensionTypeValue: number]: string },
    inventoryLevel: InventoryLevels | undefined
): boolean {
    return (productDimensions ?? []).some(dimension => {
        const selectedId = selection[dimension.DimensionTypeValue];
        const value = dimension.dimensionValuesWithInventory?.find(item => item.DimensionValue?.RecordId.toString() === selectedId);

        return value !== undefined && isDimensionValueOutOfStock(value, inventoryLevel);
    });
}

/**
 * Determines the inventory status for the product
 * @param product - The product to check
 * @param productAvailableQuantity - Array of inventory information from different locations
 * @param productDimensions - Dimensions with per-value availability, used while no variant is selected
 * @param inventoryLevel - App config inventory level setting
 * @returns The inventory status with type, cart availability, and optional store names
 */
export function getInventoryStatus(
    product: SimpleProduct | undefined,
    productAvailableQuantity: IProductInventoryInformation[] | undefined,
    productDimensions?: IDimensionForSelectedVariant[],
    inventoryLevel?: InventoryLevels
): IInventoryStatus {
    if (!product) {
        return { type: 'out-of-stock', canAddToCart: false };
    }

    // Check if this is a to-order item
    const isToOrder = getExtensionPropertyBoolean(product.ExtensionProperties, 'GHA_ToOrder', false);

    if (isToOrder) {
        return { type: 'to-order', canAddToCart: true };
    }

    // No variant selected yet: add to cart stays enabled so the click can prompt for the missing dimension
    if (hasUnselectedDimensions(product)) {
        return hasAnyVariantInStock(productDimensions, inventoryLevel)
            ? { type: 'select-variant', canAddToCart: true }
            : { type: 'out-of-stock', canAddToCart: false };
    }

    // Check for central warehouse inventory
    const hasCentralWarehouseInventory = checkCentralWarehouseInventory(productAvailableQuantity);

    if (hasCentralWarehouseInventory) {
        return { type: 'central-warehouse', canAddToCart: true };
    }

    // Check for store inventory
    const storeNames = getStoresWithInventory(productAvailableQuantity);

    if (storeNames && storeNames.length > 0) {
        return { type: 'in-store', canAddToCart: true, storeNames };
    }

    // No inventory anywhere
    return { type: 'out-of-stock', canAddToCart: false };
}

/**
 * True when any dimension value is not flagged out of stock. Missing availability data counts as in stock.
 */
function hasAnyVariantInStock(
    productDimensions: IDimensionForSelectedVariant[] | undefined,
    inventoryLevel: InventoryLevels | undefined
): boolean {
    const values = (productDimensions ?? []).reduce<ProductDimensionValueInventoryAvailability[]>(
        (all, dimension) => all.concat(dimension.dimensionValuesWithInventory ?? []),
        []
    );

    if (values.length === 0) {
        return true;
    }

    return values.some(value => !isDimensionValueOutOfStock(value, inventoryLevel));
}

/**
 * Checks if there is inventory in the central warehouse (1001)
 * @param productAvailableQuantity - Array of inventory information
 * @returns True if central warehouse has available inventory
 */
export function getCentralWarehouseInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): number {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return 0;
    }

    // Find the central warehouse inventory record and return the available quantity
    const centralWarehouse = productAvailableQuantity.find(inventory => {
        const inventLocationId = inventory.InventLocationId;
        return inventLocationId === CENTRAL_WAREHOUSE_ID;
    });

    if (!centralWarehouse) {
        return 0;
    }

    const isAvailable = centralWarehouse.IsProductAvailable ?? false;
    const availableQty = centralWarehouse.ProductAvailableQuantity?.AvailableQuantity ?? 0;

    return isAvailable && availableQty > 0 ? availableQty : 0;
}

/**
 * Checks if there is inventory in the central warehouse (1001)
 * @param productAvailableQuantity - Array of inventory information
 * @returns True if central warehouse has available inventory
 */
function checkCentralWarehouseInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): boolean {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return false;
    }

    // Check if any of the inventory records is for central warehouse with available quantity
    return productAvailableQuantity.some(inventory => {
        const isAvailable = inventory.IsProductAvailable ?? false;
        const availableQty = inventory.ProductAvailableQuantity?.AvailableQuantity ?? 0;
        const inventLocationId = inventory.InventLocationId;

        return inventLocationId === CENTRAL_WAREHOUSE_ID && isAvailable && availableQty > 0;
    });
}

/**
 * Gets list of store names that have inventory (excluding central warehouse)
 * @param productAvailableQuantity - Array of inventory information
 * @returns Array of store location IDs with inventory, or undefined if none found
 */
function getStoresWithInventory(productAvailableQuantity: IProductInventoryInformation[] | undefined): string[] | undefined {
    if (!productAvailableQuantity || productAvailableQuantity.length === 0) {
        return undefined;
    }

    const storeNames: string[] = [];

    for (const inventory of productAvailableQuantity) {
        const isAvailable = inventory.IsProductAvailable ?? false;
        const availableQty = inventory.ProductAvailableQuantity?.AvailableQuantity ?? 0;
        const inventLocationId = inventory.InventLocationId;

        // Skip central warehouse
        if (inventLocationId === CENTRAL_WAREHOUSE_ID) {
            continue;
        }

        // If it's a store with inventory, add it
        if (inventLocationId && isAvailable && availableQty > 0) {
            storeNames.push(inventLocationId);
        }
    }

    return storeNames.length > 0 ? storeNames : undefined;
}
